import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { camelCase } from 'lodash-es';

import { Button, Flex } from '@chakra-ui/react';

import { getSkillLocaleLabelMapping, transformRawDurationFilter } from '~/features/catalog/catalog-filter-data';
import { RAW_DURATION_KEYS, RAW_DURATION_REVERSE_MAPPING } from '~/features/catalog/constants';
import { useCatalogFilterLocaleMetadata } from '~/features/catalog/hooks/use-catalog-filter-locale-metadata';
import { Filters } from '~/features/catalog/models/filter';
import { clearAllFilters, updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { useI18n } from '~/hooks/use-i18n';

import { Chip } from './chip';

const SIMPLE_FILTERS = ['price', 'difficulty', 'scope', 'school'] as const;

export function CatalogFilterChips({ selectedFilters }: { selectedFilters: Filters }) {
  const router = useRouter();
  const { t, i18n } = useI18n();
  const { catalogFilterLocaleMetadata } = useCatalogFilterLocaleMetadata(i18n.language);
  const skillLocaleLabelMapping = getSkillLocaleLabelMapping(catalogFilterLocaleMetadata?.skills ?? []);
  const rawDurationFiltersCopy = selectedFilters.rawDuration ? [...selectedFilters.rawDuration] : undefined;
  const transformedRawDurationFilter = transformRawDurationFilter(rawDurationFiltersCopy);

  function handleCloseClick(filterType: keyof Filters, filter: string) {
    let newFilterParam = selectedFilters[filterType]?.filter((f) => f !== filter);

    updateCatalogUrlState({
      router: router,
      updatedRouteParameters: {
        [filterType]: newFilterParam?.join(',') || undefined,
      },
    });
  }

  const specialMappings: Partial<Record<keyof Filters, Record<string, string>>> = useMemo(
    () => ({
      type: {
        course: t('program.semanticType.course'),
        'nanodegree program': t('program.semanticType.degree'),
      },
      availability: {
        'Enroll Now': t('catalog.filters.option.enrollNow'),
        'Request Access': t('catalog.filters.option.requestAccess'),
      },
      rawDuration: {
        [RAW_DURATION_KEYS.LT20]: t('catalog.filters.option.raw.lessThan20'),
        [RAW_DURATION_KEYS.MID]: t('catalog.filters.option.raw.between20And60'),
        [RAW_DURATION_KEYS.GT60]: t('catalog.filters.option.raw.greaterThan60'),
      },
      skill: skillLocaleLabelMapping,
    }),
    [t, skillLocaleLabelMapping]
  );

  const getFilterDisplayText = (filterType: keyof Filters, filterValue: string): string => {
    if (SIMPLE_FILTERS.includes(filterType)) return t(`catalog.filters.option.${camelCase(filterValue)}`);

    return specialMappings[filterType]?.[filterValue] || filterValue;
  };

  const isClearAllRendered = Object.values(selectedFilters).some(Boolean);

  return (
    <Flex flexWrap='wrap' gap='8px'>
      {selectedFilters.price?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('price', filter)}
          closable
          onCloseClick={() => handleCloseClick('price', filter)}
        />
      ))}
      {selectedFilters.availability?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('availability', filter)}
          closable
          onCloseClick={() => handleCloseClick('availability', filter)}
        />
      ))}
      {selectedFilters.school?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('school', filter)}
          closable
          onCloseClick={() => handleCloseClick('school', filter)}
        />
      ))}
      {selectedFilters.skill?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('skill', filter)}
          closable
          onCloseClick={() => handleCloseClick('skill', filter)}
        />
      ))}
      {selectedFilters.difficulty?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('difficulty', filter)}
          closable
          onCloseClick={() => handleCloseClick('difficulty', filter)}
        />
      ))}
      {selectedFilters.duration?.map((filter) => (
        <Chip key={filter} text={filter} closable onCloseClick={() => handleCloseClick('duration', filter)} />
      ))}
      {transformedRawDurationFilter?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('rawDuration', filter)}
          closable
          onCloseClick={() => handleCloseClick('rawDuration', RAW_DURATION_REVERSE_MAPPING[filter])}
        />
      ))}
      {selectedFilters.type?.map((filter) => (
        <Chip
          key={filter}
          text={getFilterDisplayText('type', filter)}
          closable
          onCloseClick={() => handleCloseClick('type', filter)}
        />
      ))}
      {isClearAllRendered && (
        <Button
          fontSize='0.75rem'
          lineHeight='1'
          textDecoration='underline'
          variant='link'
          onClick={() => clearAllFilters(router)}
        >
          {t('catalog.filters.clearAll')}
        </Button>
      )}
    </Flex>
  );
}
